import React, { ReactNode } from "react";

import { theme } from "@ovou-inc/ovou-ui";
import { HexColorInput, HexColorPicker } from "react-colorful";

import { Box, Flex } from "../Container";
import { Button as ChakraButton } from "../FormUtils";
import NewModal from "../NewModal/NewModal";
import { Heading, Text } from "../Typography";

import { brandBlack, brandWhite, grey1, grey3, grey4 } from "./constant";
import { addSharpToColorHex, Shade } from "utils/colorUtils";
import { Shimmer } from "./Shimmer";

export interface IShimmerColors {
  primaryColor: string;
  secondaryColor: string;
  primaryShade: Shade;
  secondaryShade: Shade;
}

export interface IBaseColorPickerModalProps {
  title: string;
  description: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  isDisabled?: boolean;
  /** The color being edited, normalized to `#hex`. */
  currentColor: string;
  onColorChange: (color: string) => void;
  shimmerColors: IShimmerColors;
  /** Show the "Gradient code" field (the card-background / Primary slot). */
  allowGradient?: boolean;
  gradient?: string;
  onGradientChange?: (value: string) => void;
  headerContent?: ReactNode;
}

/**
 * Color picker modal: a live mini-card preview (Shimmer) beside a
 * saturation/hue picker, a hex input, and an optional gradient field. Ported
 * from front-v2's BaseColorPickerModal and extended with the hex + gradient
 * fields the template editor needs.
 */
export const BaseColorPickerModal: React.FC<IBaseColorPickerModalProps> = ({
  title,
  description,
  isOpen,
  onClose,
  onSave,
  isDisabled = false,
  currentColor,
  onColorChange,
  shimmerColors,
  allowGradient = false,
  gradient = "",
  onGradientChange,
  headerContent,
}) => {
  const shimmerProps = {
    border: grey3,
    width: "120",
    primaryColor: addSharpToColorHex(shimmerColors.primaryColor),
    secondaryColor: addSharpToColorHex(shimmerColors.secondaryColor),
    primaryHeadColor:
      shimmerColors.primaryShade === "light" ? brandBlack : brandWhite,
    primarySubtitleColor:
      shimmerColors.primaryShade === "light" ? grey1 : grey4,
    secondaryHeadColor:
      shimmerColors.secondaryShade === "light" ? grey1 : grey4,
  };

  const titleNode = (
    <Box>
      <Heading
        as="p"
        fontSize="21px"
        fontWeight={800}
        color={theme.palette.brand.primary.black}
      >
        {title}
      </Heading>
      <Text
        as="p"
        fontSize="16px"
        fontWeight={400}
        mt="4px"
        color={theme.palette.ui.greys.grey1}
      >
        {description}
      </Text>
    </Box>
  );

  const body = (
    <Flex direction="column" gap="24px" pb="8px">
      {headerContent}

      <Flex gap="16px" alignItems="flex-start" width="100%">
        <Box flexShrink={0}>
          <Shimmer {...shimmerProps} />
        </Box>
        <Box
          flex="1"
          minWidth={0}
          sx={{
            "& .react-colorful": { width: "100%", height: "147px" },
            "& .react-colorful__saturation": { borderRadius: "4px" },
            "& .react-colorful__hue": {
              height: "14px",
              borderRadius: "5px",
              marginTop: "16px",
            },
            "& .react-colorful__pointer": { width: "24px", height: "24px" },
          }}
        >
          <HexColorPicker color={currentColor} onChange={onColorChange} />
        </Box>
      </Flex>

      <Flex direction="column" gap="24px" width="100%">
        <Flex alignItems="center" justifyContent="space-between" gap="16px">
          <Text
            fontSize="14px"
            fontWeight={600}
            color={theme.palette.brand.primary.black}
          >
            Enter Hex code:
          </Text>
          <Flex
            alignItems="center"
            width="151px"
            borderRadius="8px"
            overflow="hidden"
            border={`1px solid ${theme.palette.ui.greys.grey3}`}
          >
            <Box
              flexShrink={0}
              width="48px"
              height="40px"
              background={currentColor}
            />
            <HexColorInput
              color={currentColor}
              onChange={onColorChange}
              prefixed
              aria-label="Hex color"
              style={{
                flex: 1,
                minWidth: 0,
                height: "40px",
                padding: "0 12px",
                border: "none",
                outline: "none",
                fontSize: "16px",
                color: theme.palette.ui.greys.grey1,
              }}
            />
          </Flex>
        </Flex>

        {allowGradient && (
          <Box>
            <input
              aria-label="Gradient code"
              placeholder="Gradient code"
              value={gradient}
              onChange={e => onGradientChange?.(e.target.value)}
              style={{
                width: "100%",
                border: "none",
                borderBottom: `1px solid ${theme.palette.ui.greys.grey4}`,
                padding: "8px 0",
                fontSize: "16px",
                color: theme.palette.brand.primary.black,
                background: "transparent",
                outline: "none",
              }}
            />
            <Text
              fontSize="12px"
              mt="8px"
              color={theme.palette.brand.primary.black}
            >
              Got a gradient code? Paste it here and save to apply it to your
              profile.
            </Text>
          </Box>
        )}
      </Flex>
    </Flex>
  );

  const footer = (
    <Flex width="100%" gap="8px">
      <ChakraButton
        flex="1"
        height="40px"
        borderRadius="8px"
        variant="outline"
        borderColor={theme.palette.ui.greys.grey3}
        color={theme.palette.brand.primary.black}
        fontFamily="Inter"
        fontWeight={800}
        fontSize="14px"
        onClick={onClose}
        _hover={{ bg: "transparent" }}
      >
        Cancel
      </ChakraButton>
      <ChakraButton
        flex="1"
        height="40px"
        borderRadius="8px"
        bg={theme.palette.brand.primary.orange}
        color={theme.palette.brand.primary.white}
        fontFamily="Inter"
        fontWeight={800}
        fontSize="14px"
        onClick={onSave}
        isDisabled={isDisabled}
        _hover={{ bg: theme.palette.brand.primary.orange, opacity: 0.9 }}
        _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
      >
        Save
      </ChakraButton>
    </Flex>
  );

  return (
    <NewModal
      isOpen={isOpen}
      onClose={onClose}
      title={titleNode}
      isCentered
      closeButtonStyle={{ display: "none" }}
      // Uniform 24px padding: own the horizontal padding on the content and
      // zero the inner header/body/footer so all three align at one 24px edge
      // (the shared default stacks 22px content + 16px body = 38px).
      modalContentStyle={{ paddingX: "24px" }}
      modalHeaderStyle={{ px: 0, pt: "24px", pb: "16px" }}
      bodyStyle={{ px: 0 }}
      modalFooterStyle={{ px: 0, pt: "8px", pb: "24px" }}
      modalBody={body}
      modalFooter={footer}
    />
  );
};
